import { useEffect, useState } from "react";
import {
  Users,
  BookOpen,
  Upload,
  Download,
  CheckCircle,
  XCircle,
  Send,
  Loader,
  Trash2,
  FileSpreadsheet,
  Bell,
  ChevronLeft,
  ChevronRight,
  Search,
  X,
} from "lucide-react";
import { adminApi, coursesApi, bulkApi, showSuccess, showError } from "../lib";

const BulkOperationsPage = () => {
  const [activeTab, setActiveTab] = useState<"users" | "courses" | "enroll" | "notifications">("users");
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState<any[]>([]);
  const [courses, setCourses] = useState<any[]>([]);
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
  const [selectedCourses, setSelectedCourses] = useState<string[]>([]);
  const [selectedCourseId, setSelectedCourseId] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const [userUpdateData, setUserUpdateData] = useState({
    isActive: true,
    role: "",
  });

  const [notificationData, setNotificationData] = useState({
    title: "",
    message: "",
    type: "info",
  });

  useEffect(() => {
    fetchData();
  }, [activeTab]);

  const fetchData = async () => {
    try {
      if (activeTab === "users") {
        const result = await adminApi.getUsers({ limit: 100 });
        if (result.success) {
          setUsers(result.data || []);
        }
      } else if (activeTab === "courses" || activeTab === "enroll") {
        const result = await coursesApi.getCourses({ limit: 100 });
        if (result.success) {
          setCourses(result.data || result.courses || []);
        }
      }
    } catch (err: any) {
      console.error("Failed to fetch data:", err);
    }
  };

  const filteredUsers = users.filter(user => 
    user.firstName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.lastName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredCourses = courses.filter(course =>
    course.title?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const toggleUserSelection = (userId: string) => {
    setSelectedUsers((prev) =>
      prev.includes(userId) ? prev.filter((id) => id !== userId) : [...prev, userId]
    );
  };

  const toggleCourseSelection = (courseId: string) => {
    setSelectedCourses((prev) =>
      prev.includes(courseId) ? prev.filter((id) => id !== courseId) : [...prev, courseId]
    );
  };

  const selectAllUsers = () => {
    if (selectedUsers.length === filteredUsers.length) {
      setSelectedUsers([]);
    } else {
      setSelectedUsers(filteredUsers.map((u) => u._id));
    }
  };

  const selectAllCourses = () => {
    if (selectedCourses.length === filteredCourses.length) {
      setSelectedCourses([]);
    } else {
      setSelectedCourses(filteredCourses.map((c) => c._id));
    }
  };

  const handleBulkUserAction = async (action: string) => {
    if (selectedUsers.length === 0) {
      showError("Please select at least one user");
      return;
    }
    setLoading(true);
    try {
      switch (action) {
        case "activate":
          await bulkApi.bulkUpdateUsers(selectedUsers, { isActive: true });
          showSuccess(`${selectedUsers.length} users activated`);
          break;
        case "deactivate":
          await bulkApi.bulkUpdateUsers(selectedUsers, { isActive: false });
          showSuccess(`${selectedUsers.length} users deactivated`);
          break;
        case "delete":
          if (!confirm(`Delete ${selectedUsers.length} users? This cannot be undone.`)) {
            setLoading(false);
            return;
          }
          await bulkApi.bulkDeleteUsers(selectedUsers);
          showSuccess(`${selectedUsers.length} users deleted`);
          break;
        case "updateRole":
          if (userUpdateData.role) {
            await bulkApi.bulkUpdateUsers(selectedUsers, { role: userUpdateData.role });
            showSuccess(`Role updated for ${selectedUsers.length} users`);
          }
          break;
      }
      setSelectedUsers([]);
      fetchData();
    } catch (err: any) {
      showError(err.message || "Action failed");
    } finally {
      setLoading(false);
    }
  };

  const handleBulkCourseAction = async (action: string) => {
    if (selectedCourses.length === 0) {
      showError("Please select at least one course");
      return;
    }
    setLoading(true);
    try {
      switch (action) {
        case "publish":
          await bulkApi.bulkPublishCourses(selectedCourses);
          showSuccess(`${selectedCourses.length} courses published`);
          break;
        case "unpublish":
          await bulkApi.bulkUnpublishCourses(selectedCourses);
          showSuccess(`${selectedCourses.length} courses unpublished`);
          break;
        case "delete":
          if (!confirm(`Delete ${selectedCourses.length} courses? This cannot be undone.`)) {
            setLoading(false);
            return;
          }
          await bulkApi.bulkDeleteCourses(selectedCourses);
          showSuccess(`${selectedCourses.length} courses deleted`);
          break;
        case "feature":
          await bulkApi.bulkUpdateCourses(selectedCourses, { isFeatured: true });
          showSuccess(`${selectedCourses.length} courses featured`);
          break;
      }
      setSelectedCourses([]);
      fetchData();
    } catch (err: any) {
      showError(err.message || "Action failed");
    } finally {
      setLoading(false);
    }
  };

  const handleBulkEnroll = async () => {
    if (selectedUsers.length === 0) {
      showError("Please select at least one user");
      return;
    }
    if (!selectedCourseId) {
      showError("Please select a course");
      return;
    }
    setLoading(true);
    try {
      await bulkApi.bulkEnrollUsers(selectedUsers, selectedCourseId);
      showSuccess(`${selectedUsers.length} users enrolled`);
      setSelectedUsers([]);
      setSelectedCourseId("");
    } catch (err: any) {
      showError(err.message || "Enrollment failed");
    } finally {
      setLoading(false);
    }
  };

  const handleBulkNotification = async () => {
    if (selectedUsers.length === 0) {
      showError("Please select at least one user");
      return;
    }
    if (!notificationData.title || !notificationData.message) {
      showError("Please fill in notification details");
      return;
    }
    setLoading(true);
    try {
      await bulkApi.bulkSendNotifications(selectedUsers, notificationData);
      showSuccess(`Notification sent to ${selectedUsers.length} users`);
      setNotificationData({ title: "", message: "", type: "info" });
    } catch (err: any) {
      showError(err.message || "Failed to send notifications");
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async (type: string, format: string) => {
    setLoading(true);
    try {
      let data: any;
      if (type === "users") {
        const result = await bulkApi.exportUsers({ format });
        data = result.data;
      } else {
        const result = await bulkApi.exportCourses({ format });
        data = result.data;
      }

      if (format === "csv") {
        // For CSV export, data is already a CSV string from the API
        const csvContent = typeof data === 'string' ? data : convertToCSV(data);
        const blob = new Blob([csvContent], { type: "text/csv" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `${type}_export.csv`;
        a.click();
      } else {
        // For JSON export, stringify the data
        const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `${type}_export.json`;
        a.click();
      }
      showSuccess("Export successful");
    } catch (err: any) {
      showError(err.message || "Export failed");
    } finally {
      setLoading(false);
    }
  };

  const convertToCSV = (data: any[]): string => {
    if (!data || data.length === 0) return "";
    const headers = Object.keys(data[0]);
    const rows = data.map((item) =>
      headers.map((h) => JSON.stringify(item[h] ?? "")).join(",")
    );
    return [headers.join(","), ...rows].join("\n");
  };

  const tabs = [
    { id: "users", label: "Users", icon: Users },
    { id: "courses", label: "Courses", icon: BookOpen },
    { id: "enroll", label: "Enroll", icon: Upload },
    { id: "notifications", label: "Notifications", icon: Bell },
  ];

  return (
    <main className="min-h-screen p-3 bg-gray-50 sm:p-4 md:p-6">
      <div className="mx-auto max-w-7xl">
        {/* Header */}
        <div className="mb-5 sm:mb-6">
          <h1 className="text-lg font-bold text-gray-900 sm:text-xl md:text-2xl">Bulk Operations</h1>
          <p className="text-xs text-gray-500 sm:text-sm">Perform bulk actions on users and courses</p>
        </div>

        {/* Mobile Tab Selector */}
        <div className="block mb-4 lg:hidden">
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="flex items-center justify-between w-full px-4 py-3 bg-white border border-gray-200 rounded-lg"
          >
            <div className="flex items-center gap-2">
              {tabs.find(t => t.id === activeTab)?.icon && 
                (() => {
                  const Icon = tabs.find(t => t.id === activeTab)!.icon;
                  return <Icon className="w-4 h-4 text-primary" />;
                })()}
              <span className="text-sm font-medium text-gray-900">
                {tabs.find(t => t.id === activeTab)?.label}
              </span>
            </div>
            {mobileMenuOpen ? <ChevronRight className="w-4 h-4 text-gray-400" /> : <ChevronLeft className="w-4 h-4 text-gray-400" />}
          </button>

          {mobileMenuOpen && (
            <div className="mt-2 overflow-hidden bg-white border border-gray-200 rounded-lg">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                const isActive = activeTab === tab.id;
                return (
                  <button
                    key={tab.id}
                    onClick={() => {
                      setActiveTab(tab.id as any);
                      setMobileMenuOpen(false);
                    }}
                    className={`w-full flex items-center gap-3 px-4 py-3 text-sm transition-colors ${
                      isActive
                        ? "bg-primary/10 text-primary font-medium"
                        : "text-gray-600 hover:bg-gray-50"
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                    {tab.label}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Desktop Tabs */}
        <div className="hidden mb-6 overflow-x-auto border-b border-gray-200 lg:flex">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as any)}
                className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition whitespace-nowrap ${
                  activeTab === tab.id
                    ? "border-primary text-primary"
                    : "border-transparent text-gray-500 hover:text-gray-700"
                }`}
              >
                <Icon className="w-4 h-4" /> {tab.label}
              </button>
            );
          })}
        </div>

        {/* Content Card */}
        <div className="overflow-hidden bg-white border border-gray-200 rounded-xl">
          <div className="p-4 sm:p-5">
            {activeTab === "users" && (
              <div>
                {/* Search and Export Bar */}
                <div className="flex flex-col gap-3 mb-4 sm:flex-row sm:items-center sm:justify-between">
                  <div className="flex flex-wrap items-center gap-2">
                    <button
                      onClick={selectAllUsers}
                      className="px-3 py-1.5 text-xs border rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      {selectedUsers.length === filteredUsers.length ? "Deselect All" : "Select All"}
                    </button>
                    <span className="text-xs text-gray-500">
                      {selectedUsers.length} selected
                    </span>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <div className="relative flex-1 sm:flex-none">
                      <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
                      <input
                        type="text"
                        placeholder="Search users..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="pl-8 pr-3 py-1.5 text-xs border border-gray-200 rounded-lg w-full sm:w-48"
                      />
                    </div>
                    <button
                      onClick={() => handleExport("users", "json")}
                      disabled={loading}
                      className="flex items-center gap-1 px-3 py-1.5 text-xs border rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      <Download className="w-3 h-3" /> JSON
                    </button>
                    <button
                      onClick={() => handleExport("users", "csv")}
                      disabled={loading}
                      className="flex items-center gap-1 px-3 py-1.5 text-xs border rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      <FileSpreadsheet className="w-3 h-3" /> CSV
                    </button>
                  </div>
                </div>

                {/* Users Table - Horizontal Scroll on Mobile */}
                <div className="overflow-x-auto border border-gray-200 rounded-lg">
                  <table className="w-full text-sm min-w-[500px]">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="w-8 px-3 py-2 font-medium text-left text-gray-600">
                          <input
                            type="checkbox"
                            checked={selectedUsers.length === filteredUsers.length && filteredUsers.length > 0}
                            onChange={selectAllUsers}
                          />
                        </th>
                        <th className="px-3 py-2 font-medium text-left text-gray-600">Name</th>
                        <th className="px-3 py-2 font-medium text-left text-gray-600">Email</th>
                        <th className="px-3 py-2 font-medium text-left text-gray-600">Role</th>
                        <th className="px-3 py-2 font-medium text-left text-gray-600">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredUsers.map((user) => (
                        <tr key={user._id} className="border-t border-gray-200 hover:bg-gray-50">
                          <td className="px-3 py-2">
                            <input
                              type="checkbox"
                              checked={selectedUsers.includes(user._id)}
                              onChange={() => toggleUserSelection(user._id)}
                            />
                          </td>
                          <td className="px-3 py-2 text-sm">
                            {user.firstName} {user.lastName}
                          </td>
                          <td className="px-3 py-2 text-xs text-gray-500">{user.email}</td>
                          <td className="px-3 py-2">
                            <span className="text-xs bg-gray-100 px-2 py-0.5 rounded">
                              {user.role}
                            </span>
                          </td>
                          <td className="px-3 py-2">
                            <span
                              className={`text-xs ${
                                user.isActive ? "text-green-600" : "text-red-600"
                              }`}
                            >
                              {user.isActive ? "Active" : "Inactive"}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {filteredUsers.length === 0 && (
                    <div className="py-8 text-sm text-center text-gray-500">
                      No users found
                    </div>
                  )}
                </div>

                {/* Action Buttons */}
                <div className="flex flex-wrap gap-2 mt-4">
                  <button
                    onClick={() => handleBulkUserAction("activate")}
                    disabled={loading || selectedUsers.length === 0}
                    className="flex items-center gap-1 px-3 py-1.5 text-xs bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 transition-colors"
                  >
                    <CheckCircle className="w-3 h-3" /> Activate
                  </button>
                  <button
                    onClick={() => handleBulkUserAction("deactivate")}
                    disabled={loading || selectedUsers.length === 0}
                    className="flex items-center gap-1 px-3 py-1.5 text-xs bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 disabled:opacity-50 transition-colors"
                  >
                    <XCircle className="w-3 h-3" /> Deactivate
                  </button>
                  <select
                    value={userUpdateData.role}
                    onChange={(e) => setUserUpdateData({ ...userUpdateData, role: e.target.value })}
                    className="px-3 py-1.5 text-xs border rounded-lg"
                  >
                    <option value="">Select Role</option>
                    <option value="student">Student</option>
                    <option value="mentor">Mentor</option>
                    <option value="instructor">Instructor</option>
                  </select>
                  <button
                    onClick={() => handleBulkUserAction("updateRole")}
                    disabled={loading || selectedUsers.length === 0 || !userUpdateData.role}
                    className="flex items-center gap-1 px-3 py-1.5 text-xs bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
                  >
                    Update Role
                  </button>
                  <button
                    onClick={() => handleBulkUserAction("delete")}
                    disabled={loading || selectedUsers.length === 0}
                    className="flex items-center gap-1 px-3 py-1.5 text-xs bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 transition-colors"
                  >
                    <Trash2 className="w-3 h-3" /> Delete
                  </button>
                </div>
              </div>
            )}

            {activeTab === "courses" && (
              <div>
                {/* Search and Export Bar */}
                <div className="flex flex-col gap-3 mb-4 sm:flex-row sm:items-center sm:justify-between">
                  <div className="flex flex-wrap items-center gap-2">
                    <button
                      onClick={selectAllCourses}
                      className="px-3 py-1.5 text-xs border rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      {selectedCourses.length === filteredCourses.length ? "Deselect All" : "Select All"}
                    </button>
                    <span className="text-xs text-gray-500">
                      {selectedCourses.length} selected
                    </span>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <div className="relative flex-1 sm:flex-none">
                      <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
                      <input
                        type="text"
                        placeholder="Search courses..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="pl-8 pr-3 py-1.5 text-xs border border-gray-200 rounded-lg w-full sm:w-48"
                      />
                    </div>
                    <button
                      onClick={() => handleExport("courses", "json")}
                      disabled={loading}
                      className="flex items-center gap-1 px-3 py-1.5 text-xs border rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      <Download className="w-3 h-3" /> JSON
                    </button>
                    <button
                      onClick={() => handleExport("courses", "csv")}
                      disabled={loading}
                      className="flex items-center gap-1 px-3 py-1.5 text-xs border rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      <FileSpreadsheet className="w-3 h-3" /> CSV
                    </button>
                  </div>
                </div>

                {/* Courses Table */}
                <div className="overflow-x-auto border border-gray-200 rounded-lg">
                  <table className="w-full text-sm min-w-[600px]">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="w-8 px-3 py-2 font-medium text-left text-gray-600">
                          <input
                            type="checkbox"
                            checked={selectedCourses.length === filteredCourses.length && filteredCourses.length > 0}
                            onChange={selectAllCourses}
                          />
                        </th>
                        <th className="px-3 py-2 font-medium text-left text-gray-600">Title</th>
                        <th className="px-3 py-2 font-medium text-left text-gray-600">Instructor</th>
                        <th className="px-3 py-2 font-medium text-left text-gray-600">Status</th>
                        <th className="px-3 py-2 font-medium text-left text-gray-600">Price</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredCourses.map((course) => (
                        <tr key={course._id} className="border-t border-gray-200 hover:bg-gray-50">
                          <td className="px-3 py-2">
                            <input
                              type="checkbox"
                              checked={selectedCourses.includes(course._id)}
                              onChange={() => toggleCourseSelection(course._id)}
                            />
                          </td>
                          <td className="px-3 py-2 text-sm">{course.title}</td>
                          <td className="px-3 py-2 text-xs text-gray-500">
                            {course.instructor?.firstName} {course.instructor?.lastName}
                          </td>
                          <td className="px-3 py-2">
                            <span
                              className={`text-xs ${
                                course.isPublished || course.status === "published"
                                  ? "text-green-600"
                                  : "text-yellow-600"
                              }`}
                            >
                              {course.isPublished || course.status === "published"
                                ? "Published"
                                : "Draft"}
                            </span>
                          </td>
                          <td className="px-3 py-2 text-sm">${course.price}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {filteredCourses.length === 0 && (
                    <div className="py-8 text-sm text-center text-gray-500">
                      No courses found
                    </div>
                  )}
                </div>

                {/* Action Buttons */}
                <div className="flex flex-wrap gap-2 mt-4">
                  <button
                    onClick={() => handleBulkCourseAction("publish")}
                    disabled={loading || selectedCourses.length === 0}
                    className="flex items-center gap-1 px-3 py-1.5 text-xs bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 transition-colors"
                  >
                    <CheckCircle className="w-3 h-3" /> Publish
                  </button>
                  <button
                    onClick={() => handleBulkCourseAction("unpublish")}
                    disabled={loading || selectedCourses.length === 0}
                    className="flex items-center gap-1 px-3 py-1.5 text-xs bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 disabled:opacity-50 transition-colors"
                  >
                    <XCircle className="w-3 h-3" /> Unpublish
                  </button>
                  <button
                    onClick={() => handleBulkCourseAction("feature")}
                    disabled={loading || selectedCourses.length === 0}
                    className="flex items-center gap-1 px-3 py-1.5 text-xs bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
                  >
                    Feature
                  </button>
                  <button
                    onClick={() => handleBulkCourseAction("delete")}
                    disabled={loading || selectedCourses.length === 0}
                    className="flex items-center gap-1 px-3 py-1.5 text-xs bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 transition-colors"
                  >
                    <Trash2 className="w-3 h-3" /> Delete
                  </button>
                </div>
              </div>
            )}

            {activeTab === "enroll" && (
              <div>
                <div className="mb-4">
                  <label className="block mb-1 text-xs font-medium text-gray-700">
                    Select Course
                  </label>
                  <select
                    value={selectedCourseId}
                    onChange={(e) => setSelectedCourseId(e.target.value)}
                    className="w-full max-w-xs px-3 py-2 text-sm border border-gray-200 rounded-lg"
                  >
                    <option value="">Choose a course...</option>
                    {courses.map((course) => (
                      <option key={course._id} value={course._id}>
                        {course.title}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                  <div className="flex flex-wrap items-center gap-2">
                    <button
                      onClick={selectAllUsers}
                      className="px-3 py-1.5 text-xs border rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      {selectedUsers.length === filteredUsers.length ? "Deselect All" : "Select All"}
                    </button>
                    <span className="text-xs text-gray-500">
                      {selectedUsers.length} users selected
                    </span>
                  </div>
                  <div className="relative">
                    <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Search users..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-8 pr-3 py-1.5 text-xs border border-gray-200 rounded-lg w-full sm:w-48"
                    />
                  </div>
                </div>

                <div className="mb-4 overflow-hidden overflow-y-auto border border-gray-200 rounded-lg max-h-64">
                  <table className="w-full text-sm min-w-[400px]">
                    <thead className="sticky top-0 bg-gray-50">
                      <tr>
                        <th className="w-8 px-3 py-2 font-medium text-left text-gray-600">
                          <input
                            type="checkbox"
                            checked={selectedUsers.length === filteredUsers.length && filteredUsers.length > 0}
                            onChange={selectAllUsers}
                          />
                        </th>
                        <th className="px-3 py-2 font-medium text-left text-gray-600">Name</th>
                        <th className="px-3 py-2 font-medium text-left text-gray-600">Email</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredUsers.map((user) => (
                        <tr key={user._id} className="border-t border-gray-200 hover:bg-gray-50">
                          <td className="px-3 py-2">
                            <input
                              type="checkbox"
                              checked={selectedUsers.includes(user._id)}
                              onChange={() => toggleUserSelection(user._id)}
                            />
                          </td>
                          <td className="px-3 py-2 text-sm">{user.firstName} {user.lastName}</td>
                          <td className="px-3 py-2 text-xs text-gray-500">{user.email}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {filteredUsers.length === 0 && (
                    <div className="py-8 text-sm text-center text-gray-500">
                      No users found
                    </div>
                  )}
                </div>

                <button
                  onClick={handleBulkEnroll}
                  disabled={loading || selectedUsers.length === 0 || !selectedCourseId}
                  className="flex items-center gap-2 px-4 py-2 text-sm text-white transition-colors rounded-lg bg-primary hover:bg-primary/90 disabled:opacity-50"
                >
                  {loading ? <Loader className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                  Enroll Selected Users
                </button>
              </div>
            )}

            {activeTab === "notifications" && (
              <div>
                <div className="mb-4">
                  <label className="block mb-2 text-xs font-medium text-gray-700">
                    Select Users
                  </label>
                  <div className="flex flex-wrap items-center gap-2 mb-3">
                    <button
                      onClick={selectAllUsers}
                      className="px-3 py-1.5 text-xs border rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      {selectedUsers.length === filteredUsers.length ? "Deselect All" : "Select All"}
                    </button>
                    <span className="text-xs text-gray-500">
                      {selectedUsers.length} selected
                    </span>
                    <div className="relative ml-auto">
                      <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
                      <input
                        type="text"
                        placeholder="Search users..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="pl-8 pr-3 py-1.5 text-xs border border-gray-200 rounded-lg w-full sm:w-48"
                      />
                    </div>
                  </div>
                </div>

                <div className="mb-4 overflow-hidden overflow-y-auto border border-gray-200 rounded-lg max-h-48">
                  <table className="w-full text-sm min-w-[400px]">
                    <thead className="sticky top-0 bg-gray-50">
                      <tr>
                        <th className="w-8 px-3 py-2 font-medium text-left text-gray-600">
                          <input
                            type="checkbox"
                            checked={selectedUsers.length === filteredUsers.length && filteredUsers.length > 0}
                            onChange={selectAllUsers}
                          />
                        </th>
                        <th className="px-3 py-2 font-medium text-left text-gray-600">Name</th>
                        <th className="px-3 py-2 font-medium text-left text-gray-600">Email</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredUsers.map((user) => (
                        <tr key={user._id} className="border-t border-gray-200 hover:bg-gray-50">
                          <td className="px-3 py-2">
                            <input
                              type="checkbox"
                              checked={selectedUsers.includes(user._id)}
                              onChange={() => toggleUserSelection(user._id)}
                            />
                          </td>
                          <td className="px-3 py-2 text-sm">{user.firstName} {user.lastName}</td>
                          <td className="px-3 py-2 text-xs text-gray-500">{user.email}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {filteredUsers.length === 0 && (
                    <div className="py-8 text-sm text-center text-gray-500">
                      No users found
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-1 gap-3 mb-4 sm:grid-cols-2">
                  <div className="space-y-1">
                    <label className="text-xs font-medium text-gray-700">Title</label>
                    <input
                      type="text"
                      value={notificationData.title}
                      onChange={(e) =>
                        setNotificationData({ ...notificationData, title: e.target.value })
                      }
                      placeholder="Notification title"
                      className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg"
                    />
                  </div>
                  <div className="space-y-1">
                    <label className="text-xs font-medium text-gray-700">Type</label>
                    <select
                      value={notificationData.type}
                      onChange={(e) =>
                        setNotificationData({ ...notificationData, type: e.target.value })
                      }
                      className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg"
                    >
                      <option value="info">Info</option>
                      <option value="success">Success</option>
                      <option value="warning">Warning</option>
                      <option value="error">Error</option>
                    </select>
                  </div>
                  <div className="col-span-1 space-y-1 sm:col-span-2">
                    <label className="text-xs font-medium text-gray-700">Message</label>
                    <textarea
                      value={notificationData.message}
                      onChange={(e) =>
                        setNotificationData({ ...notificationData, message: e.target.value })
                      }
                      placeholder="Notification message"
                      className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg"
                      rows={3}
                    />
                  </div>
                </div>

                <button
                  onClick={handleBulkNotification}
                  disabled={
                    loading || selectedUsers.length === 0 || !notificationData.title || !notificationData.message
                  }
                  className="flex items-center gap-2 px-4 py-2 text-sm text-white transition-colors rounded-lg bg-primary hover:bg-primary/90 disabled:opacity-50"
                >
                  {loading ? <Loader className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  Send Notifications
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </main>
  );
};

export default BulkOperationsPage;